import { useState } from "react";
import { Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { useSite } from "../context/SiteContext";
import { Reveal, SectionHeader } from "./Reveal";
import { WhatsAppIcon } from "./Hero";

const inputCls =
    "w-full rounded-xl border border-gold/20 bg-white/5 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-500 focus:border-gold/60 focus:outline-none transition-colors";

export default function Contact() {
    const { content, t, lang } = useSite();
    const [form, setForm] = useState({ name: "", phone: "", service: "", date: "", time: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const waLink = `https://wa.me/${content?.whatsapp || ""}?text=${encodeURIComponent(
        t.wa.hero.replace("{name}", content?.business_name || "J&S Beauty Studio")
    )}`;

    const svcName = (s) => (lang === "en" && s.name_en ? s.name_en : s.name);
    const catLabel = (c) => t.cats[c] || c;

    const buildWaMessage = () =>
        [
            t.wa.greet.replace("{name}", content?.business_name || "J&S Beauty Studio"),
            form.name && `• ${t.wa.name}: ${form.name}`,
            form.phone && `• ${t.wa.phone}: ${form.phone}`,
            form.service && `• ${t.wa.service}: ${form.service}`,
            form.date && `• ${t.wa.date}: ${form.date.split("-")[lang === "en" ? 0 : 2]}/${form.date.split("-")[1]}/${form.date.split("-")[lang === "en" ? 1 : 2]}`,
            form.time && `• ${t.wa.time}: ${form.time}`,
            form.message && `• ${t.wa.message}: ${form.message}`,
        ]
            .filter(Boolean)
            .join("\n");

    const sendWhatsApp = () => {
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error(t.contact.errRequired);
            return;
        }
        window.open(`https://wa.me/${content?.whatsapp || ""}?text=${encodeURIComponent(buildWaMessage())}`, "_blank");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error(t.contact.errRequired);
            return;
        }
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success(t.contact.toastOk);
            setForm({ name: "", phone: "", service: "", date: "", time: "", message: "" });
        } catch {
            toast.error(t.contact.toastError);
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contacto" data-testid="contact-section" className="relative py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <SectionHeader eyebrow={t.contact.eyebrow} title={t.contact.title} subtitle={t.contact.subtitle} />

                <div className="mt-14 grid lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-2 flex flex-col gap-5">
                        <Reveal>
                            <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-whatsapp-button"
                                className="group flex items-center gap-5 rounded-2xl border border-gold/20 bg-surface p-6 hover:border-gold/50 hover:-translate-y-1 transition-all duration-400"
                            >
                                <span className="h-12 w-12 rounded-full bg-[#25D366]/15 flex items-center justify-center text-[#25D366] group-hover:scale-110 transition-transform">
                                    <WhatsAppIcon size={22} />
                                </span>
                                <span>
                                    <span className="block text-xs uppercase tracking-[0.25em] text-zinc-500">WhatsApp</span>
                                    <span className="block mt-1 text-amber-50 font-light">{t.contact.messageUs}</span>
                                </span>
                            </a>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <a
                                href={`tel:${(content?.phone || "").replace(/[^\d+]/g, "")}`}
                                data-testid="contact-phone-button"
                                className="group flex items-center gap-5 rounded-2xl border border-gold/20 bg-surface p-6 hover:border-gold/50 hover:-translate-y-1 transition-all duration-400"
                            >
                                <span className="h-12 w-12 rounded-full bg-gold/10 flex items-center justify-center text-gold group-hover:scale-110 transition-transform">
                                    <Phone size={20} />
                                </span>
                                <span>
                                    <span className="block text-xs uppercase tracking-[0.25em] text-zinc-500">{t.contact.phone}</span>
                                    <span className="block mt-1 text-amber-50 font-light" data-testid="contact-phone-value">{content?.phone}</span>
                                </span>
                            </a>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <div className="flex items-center gap-5 rounded-2xl border border-gold/20 bg-surface p-6">
                                <span className="h-12 w-12 rounded-full bg-gold/10 flex items-center justify-center text-gold shrink-0">
                                    <MapPin size={20} />
                                </span>
                                <span>
                                    <span className="block text-xs uppercase tracking-[0.25em] text-zinc-500">{t.contact.address}</span>
                                    <span className="block mt-1 text-amber-50 font-light" data-testid="contact-address-value">{content?.address}</span>
                                </span>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1} className="lg:col-span-3">
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="rounded-3xl border border-gold/20 bg-surface p-8 sm:p-10 h-full"
                        >
                            <div className="grid sm:grid-cols-2 gap-5">
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.contact.name}</span>
                                    <input required value={form.name} onChange={set("name")} data-testid="contact-input-name" placeholder={t.contact.namePh} className={`${inputCls} mt-1.5`} />
                                </label>
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.contact.phoneLabel}</span>
                                    <input required value={form.phone} onChange={set("phone")} data-testid="contact-input-phone" placeholder={t.contact.phonePh} className={`${inputCls} mt-1.5`} />
                                </label>
                                <label className="block sm:col-span-2">
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.contact.service}</span>
                                    <select value={form.service} onChange={set("service")} data-testid="contact-input-service" className={`${inputCls} mt-1.5 [&>option]:bg-[#121215]`}>
                                        <option value="">{t.contact.selectService}</option>
                                        {(content?.services || []).map((s) => (
                                            <option key={s.id} value={`${s.category} · ${s.name}`}>{catLabel(s.category)} · {svcName(s)}</option>
                                        ))}
                                        <option value={t.contact.other}>{t.contact.other}</option>
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.contact.date}</span>
                                    <input type="date" value={form.date} onChange={set("date")} data-testid="contact-input-date" className={`${inputCls} mt-1.5 [color-scheme:dark]`} />
                                </label>
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.contact.time}</span>
                                    <input type="time" value={form.time} onChange={set("time")} data-testid="contact-input-time" className={`${inputCls} mt-1.5 [color-scheme:dark]`} />
                                </label>
                                <label className="block sm:col-span-2">
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.contact.message}</span>
                                    <textarea rows={3} value={form.message} onChange={set("message")} data-testid="contact-input-message" placeholder={t.contact.msgPh} className={`${inputCls} mt-1.5 resize-none`} />
                                </label>
                            </div>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="contact-submit-button"
                                    className="flex-1 inline-flex items-center justify-center gap-2.5 bg-gold-gradient text-black font-semibold px-8 py-3.5 rounded-full hover:shadow-[0_0_35px_rgba(212,175,55,0.4)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                                    {sending ? t.contact.sending : t.contact.send}
                                </button>
                                <button
                                    type="button"
                                    onClick={sendWhatsApp}
                                    data-testid="contact-whatsapp-send-button"
                                    className="flex-1 inline-flex items-center justify-center gap-2.5 bg-[#25D366] text-white font-semibold px-8 py-3.5 rounded-full hover:shadow-[0_0_30px_rgba(37,211,102,0.45)] hover:-translate-y-0.5 transition-all duration-300"
                                >
                                    <WhatsAppIcon size={17} />
                                    {t.contact.sendWa}
                                </button>
                            </div>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
